import React from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { ChevronDown, Filter, X } from 'react-feather';
import { Button } from '../../../ui';
import { fieldDepth, Task, TicketConfig, TicketFieldDef, TicketOption } from '../../../Tasks/tasksApi';
import { IconButton } from './ui-pending';
import './TicketSubFilters.scss';

/**
 * Jira-style sub-filters for a task type on the unified ticket model: pick
 * the type, then "+ Add filter" offers that type's own dropdown fields (plus
 * last activity and closing reason). Values within a filter are OR'd;
 * filters are AND'd. Cascade fields sit indented under their parent.
 */


export type SubFilters = Record<string, string[]>;

interface FilterDef {
  key: string;
  label: string;
  options: TicketOption[];
  field?: TicketFieldDef;
  depth: number;
}

export const subFilterDefs = (cfg: TicketConfig): FilterDef[] => [
  { key: '__activity', label: 'Last activity', options: cfg.activities, depth: 0 },
  // The same question Renewal Insights answers: did an RM ever get through,
  // whatever happened afterwards. "Last activity" could not express it — a
  // ticket that connected and then got a follow-up left that filter — and the
  // two numbers then looked like a contradiction.
  { key: '__everActivity', label: 'Ever logged', options: cfg.activities, depth: 0 },
  { key: '__resolution', label: 'Closing reason', options: cfg.resolutions, depth: 0 },
  ...cfg.fields
    .filter((f) => (f.kind === 'select' || f.kind === 'multi') && (f.options ?? []).length > 0)
    .map((f) => ({ key: f.key, label: f.label, options: f.options ?? [], field: f, depth: fieldDepth(cfg, f) })),
];

/** Whether a task passes every active sub-filter. */
export const matchesSubFilters = (t: Task, filters: SubFilters): boolean =>
  Object.entries(filters).every(([key, values]) => {
    if (!values.length) return true;
    // "Ever logged" holds a list, so it matches when any selected value is in
    // it, unlike the single-valued filters below.
    if (key === '__everActivity') {
      const logged = t.activitiesLogged ?? [];
      return values.some((v) => logged.includes(v));
    }
    const v =
      key === '__activity'
        ? t.lastActivityKey
        : key === '__resolution'
          ? t.resolutionKey
          : t.fieldValues?.[key];
    if (Array.isArray(v)) return v.some((x) => values.includes(x));
    return typeof v === 'string' && values.includes(v);
  });

const TicketSubFilters: React.FC<{
  cfg: TicketConfig;
  filters: SubFilters;
  onChange: (next: SubFilters) => void;
  /** Tasks in the current scope, for per-option counts. */
  tasks: Task[];
}> = ({ cfg, filters, onChange, tasks }) => {
  const defs = subFilterDefs(cfg);
  const active = defs.filter((d) => d.key in filters);
  const available = defs.filter((d) => !(d.key in filters));

  const conditionOf = (d: FilterDef): string | undefined => {
    if (!d.field?.showWhen) return undefined;
    const parent = cfg.fields.find((f) => f.key === d.field!.showWhen!.field);
    const labels = d.field.showWhen.in.map((k) => parent?.options?.find((o) => o.key === k)?.label ?? k);
    return `only when ${parent?.label ?? d.field.showWhen.field} = ${labels.join(' or ')}`;
  };

  const toggle = (key: string, value: string): void => {
    const current = filters[key] ?? [];
    onChange({
      ...filters,
      [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    });
  };

  const remove = (key: string): void => {
    const next = { ...filters };
    delete next[key];
    onChange(next);
  };

  const countFor = (key: string, value: string): number =>
    tasks.filter((t) => matchesSubFilters(t, { ...filters, [key]: [value] })).length;

  return (
    // Sits inside the queue's filter toolbar, next to the type selector.
    <div className="tsf">
      {active.map((d) => {
        const values = filters[d.key] ?? [];
        const summary = !values.length
          ? 'any'
          : values.length === 1
            ? d.options.find((o) => o.key === values[0])?.label ?? values[0]
            : `${values.length} selected`;
        const condition = conditionOf(d);
        return (
          <span
            key={d.key}
            className="tsf-chip"
            title={
              d.key === '__activity'
                ? 'The most recent activity only. A ticket that connected and then got a ' +
                  'follow-up counts as Follow-up here — use "Ever logged" to find it by the ' +
                  'call instead.'
                : d.key === '__everActivity'
                  ? 'Anything logged on the ticket at any point, whatever came after. "Ever ' +
                    'logged: Connected" is the same count Renewal Insights calls "Reached at ' +
                    'least once".'
                  : undefined
            }
          >
            <UncontrolledDropdown>
              <DropdownToggle tag="button" type="button" className="tsf-chip__toggle">
                <strong>{d.label}:</strong>{' '}
                <span className={values.length ? 'tsf-chip__value' : 'tsf-chip__value tsf-chip__value--empty'}>
                  {summary}
                </span>
                <ChevronDown size={14} aria-hidden />
              </DropdownToggle>
              <DropdownMenu className="tsf-menu">
                {condition && (
                  <DropdownItem header className="tsf-menu__header">
                    {condition}
                  </DropdownItem>
                )}
                {d.options.map((o) => {
                  const checked = values.includes(o.key);
                  const count = countFor(d.key, o.key);
                  return (
                    <DropdownItem
                      key={o.key}
                      toggle={false}
                      onClick={() => toggle(d.key, o.key)}
                      className={count === 0 && !checked ? 'tsf-option tsf-option--empty' : 'tsf-option'}
                    >
                      <input type="checkbox" readOnly checked={checked} tabIndex={-1} />
                      <span className="tsf-option__label">{o.label}</span>
                      <span className="rm-caption ds-num">{count}</span>
                    </DropdownItem>
                  );
                })}
              </DropdownMenu>
            </UncontrolledDropdown>
            <IconButton
              className="tsf-chip__remove"
              aria-label={`Remove ${d.label} filter`}
              onClick={() => remove(d.key)}
            >
              <X size={14} aria-hidden />
            </IconButton>
          </span>
        );
      })}
      {available.length > 0 && (
        <UncontrolledDropdown>
          <DropdownToggle tag="button" type="button" className="ds-btn ds-btn--secondary ds-btn--sm">
            <Filter size={14} aria-hidden />
            Add filter
          </DropdownToggle>
          <DropdownMenu className="tsf-menu">
            {available.map((d) => (
              <DropdownItem key={d.key} onClick={() => onChange({ ...filters, [d.key]: [] })}>
                <span className={`tsf-depth tsf-depth--${Math.min(d.depth, 4)}`}>
                  {d.depth > 0 && <span className="ds-text-subtle">↳ </span>}
                  {d.label}
                </span>
              </DropdownItem>
            ))}
          </DropdownMenu>
        </UncontrolledDropdown>
      )}
      {active.length > 0 && (
        <Button variant="ghost" size="sm" onClick={() => onChange({})}>
          Clear all
        </Button>
      )}
    </div>
  );
};

export default TicketSubFilters;
